"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isLimitedToSharedRhino6System = exports.canChangeOrganizationBackendSystem = exports.canChangeUserBackendSystem = exports.canChangeBackendSystem = exports.hasOrganizationPermission = exports.hasUserPermission = exports.isUserChargebeeSubscriptionFree = exports.isUserOrganizationMember = exports.isChargebeeSubscriptionFree = exports.isChargebeeSubscriptionStatusFree = exports.getChargebeeSubscriptionStatus = exports.ChargebeeSubscriptionStatusEnum = void 0;
const api_platform_api_dto_v1_1 = require("@shapediver/api.platform-api-dto-v1");
/**
 * Status of a Chargebee subscription
 * @see https://apidocs.chargebee.com/docs/api/v1/subscriptions?lang=curl#subscription_status
 */
var ChargebeeSubscriptionStatusEnum;
(function (ChargebeeSubscriptionStatusEnum) {
    /** The subscription is scheduled to start at a future date. */
    ChargebeeSubscriptionStatusEnum["Future"] = "future";
    /** The subscription is in trial. */
    ChargebeeSubscriptionStatusEnum["InTrial"] = "in_trial";
    /** The subscription is active and will be charged for automatically based on the items in it. */
    ChargebeeSubscriptionStatusEnum["Active"] = "active";
    /** The subscription will be canceled at the end of the current term. */
    ChargebeeSubscriptionStatusEnum["NonRenewing"] = "non_renewing";
    /** The subscription has been canceled and is no longer in service. */
    ChargebeeSubscriptionStatusEnum["Cancelled"] = "cancelled";
    /** Unknown subscription status. This is not defined by Chargebee. */
    ChargebeeSubscriptionStatusEnum["Unknown"] = "unknown";
})(ChargebeeSubscriptionStatusEnum = exports.ChargebeeSubscriptionStatusEnum || (exports.ChargebeeSubscriptionStatusEnum = {}));
/**
 * Given a subscription get its status
 * @param subscription
 * @returns
 */
const getChargebeeSubscriptionStatus = (subscription) => {
    switch (subscription.status) {
        case ChargebeeSubscriptionStatusEnum.Future:
            return ChargebeeSubscriptionStatusEnum.Future;
        case ChargebeeSubscriptionStatusEnum.InTrial:
            return ChargebeeSubscriptionStatusEnum.InTrial;
        case ChargebeeSubscriptionStatusEnum.Active:
            return ChargebeeSubscriptionStatusEnum.Active;
        case ChargebeeSubscriptionStatusEnum.NonRenewing:
            return ChargebeeSubscriptionStatusEnum.NonRenewing;
        case ChargebeeSubscriptionStatusEnum.Cancelled:
            return ChargebeeSubscriptionStatusEnum.Cancelled;
        default:
            return ChargebeeSubscriptionStatusEnum.Unknown;
    }
};
exports.getChargebeeSubscriptionStatus = getChargebeeSubscriptionStatus;
/**
 * Check whether the subscription status counts as a "Free" subscription.
 * @param status
 * @returns
 */
const isChargebeeSubscriptionStatusFree = (status) => {
    return [
        ChargebeeSubscriptionStatusEnum.Future,
        ChargebeeSubscriptionStatusEnum.Cancelled,
        ChargebeeSubscriptionStatusEnum.Unknown
    ].includes(status);
};
exports.isChargebeeSubscriptionStatusFree = isChargebeeSubscriptionStatusFree;
/**
 * Check whether the subscriptions counts as a "Free" subscription.
 * @param subscription
 * @returns
 */
const isChargebeeSubscriptionFree = (subscription) => {
    return (0, exports.isChargebeeSubscriptionStatusFree)((0, exports.getChargebeeSubscriptionStatus)(subscription));
};
exports.isChargebeeSubscriptionFree = isChargebeeSubscriptionFree;
/**
 * Check whether the given user is member of an organization.
 */
const isUserOrganizationMember = (user) => {
    return !!user.organization_role;
};
exports.isUserOrganizationMember = isUserOrganizationMember;
/**
 * Check whether the subscription of the user or organization counts as a "Free" subscription.
 * @param user
 * @param organization must be provided in case the user is member of an organization
 * @returns
 */
const isUserChargebeeSubscriptionFree = (user, organization) => {
    if (!(0, exports.isUserOrganizationMember)(user)) {
        if (!user.chargebee_subscription)
            throw new Error('Embed field \"chargebee_subscription\" required for user but not available.');
        return (0, exports.isChargebeeSubscriptionFree)(user.chargebee_subscription);
    }
    if (!organization)
        throw new Error('Information about organization required for organization members.');
    if (!organization.chargebee_subscription)
        throw new Error('Embed field \"chargebee_subscription\" required for organization but not available.');
    return (0, exports.isChargebeeSubscriptionFree)(organization.chargebee_subscription);
};
exports.isUserChargebeeSubscriptionFree = isUserChargebeeSubscriptionFree;
/**
 * Check whether a permission is granted for the given user.
 * @param user
 * @param permission
 * @returns
 */
const hasUserPermission = (user, permission) => {
    return user.permissions.includes(permission);
};
exports.hasUserPermission = hasUserPermission;
/**
 * Check whether a permission is granted for the given organization.
 * @param organization
 * @param permission
 * @returns
 */
const hasOrganizationPermission = (organization, permission) => {
    return organization.permissions.includes(permission);
};
exports.hasOrganizationPermission = hasOrganizationPermission;
/**
 * Check whether the given user is able to change
 *   * her default backend system (in case user is not an organization member), or
 *   * her organization's default backend system (in case user is an organization member).
 *
 * The checks include whether
 *   * permission is granted to do the change, and
 *   * there are at least two backend systems to choose from.
 *
 * In case the user is a member of an organization, the organization parameter
 * must be provided.
 * @param user
 * @param organization
 * @deprecated Use canChangeUserBackendSystem or canChangeOrganizationBackendSystem instead.
 */
const canChangeBackendSystem = (user, organization) => {
    if ((0, exports.isUserOrganizationMember)(user)) {
        if (!organization)
            throw new Error('Information about organization required for organization members.');
        if (!organization.features)
            throw new Error('Property \"features"\ required but not set for organization.');
        if (organization.features.backend.length <= 1)
            return false;
        return (0, exports.hasOrganizationPermission)(organization, api_platform_api_dto_v1_1.SdPlatformPolicyPermissionsOrganization.updateOrganizationAdmin);
    }
    if (!user.features)
        throw new Error('Property \"features"\ required but not set for user.');
    if (user.features.backend.length <= 1)
        return false;
    return (0, exports.hasUserPermission)(user, api_platform_api_dto_v1_1.SdPlatformPolicyPermissionsUser.updateSelf);
};
exports.canChangeBackendSystem = canChangeBackendSystem;
/**
 * Check whether the user is able to change her default backend system.
 *
 * The checks include whether
 *   * permission is granted to do the change, and
 *   * there are at least two backend systems to choose from.
 *
 * @param user
 */
const canChangeUserBackendSystem = (user) => {
    if (!user.features)
        throw new Error('Property \"features"\ required but not set for user.');
    if (user.features.backend.length <= 1)
        return false;
    return (0, exports.hasUserPermission)(user, api_platform_api_dto_v1_1.SdPlatformPolicyPermissionsUser.updateSelf);
};
exports.canChangeUserBackendSystem = canChangeUserBackendSystem;
/**
 * Check whether the user is able to change her organization's default backend system.
 *
 * The checks include whether
 *   * permission is granted to do the change, and
 *   * there are at least two backend systems to choose from.
 *
 * @param organization
 */
const canChangeOrganizationBackendSystem = (organization) => {
    if (!organization)
        throw new Error('Information about organization required for organization members.');
    if (!organization.features)
        throw new Error('Property \"features"\ required but not set for organization.');
    if (organization.features.backend.length <= 1)
        return false;
    return (0, exports.hasOrganizationPermission)(organization, api_platform_api_dto_v1_1.SdPlatformPolicyPermissionsOrganization.updateOrganizationAdmin);
};
exports.canChangeOrganizationBackendSystem = canChangeOrganizationBackendSystem;
/**
 * Check whether the given user is limited to the shared Rhino 6 system, and can
 * not switch to another geometry backend system.
 *
 * @param user
 * @returns
 */
const isLimitedToSharedRhino6System = (user) => {
    // Note that user.features is mapped from organization.features for organization members.
    if (!user.features)
        throw new Error('Property \"features"\ required but not set for user.');
    return user.features.backend.length === 1 && user.features.backend[0] === 'sdeuc1';
};
exports.isLimitedToSharedRhino6System = isLimitedToSharedRhino6System;
//# sourceMappingURL=conditions.js.map